<template>
  <div class="task">
    <div class="thistask"
         :class="[{selected: selected, hovered: hovered || draggedOver }, density]"
         v-if="!jsTask.isRoot"
         @mouseover="hoverTask"
         @mouseout="unhoverTask"
         @dragover="dragOver"
         @dragleave="dragLeave"
         @drop="drop">
      <span class="debugid" v-if="debug && !jsTask.isRoot">{{ jsTask.id }} : {{ jsTask.gTask.position }}</span>
      <div class="draghandle"
           ref="draghandle"
           v-if="density !== 'mobile'"
           draggable="true"
           @mousedown="noteUserAction"
           @dragstart="startDrag"
           @dragend="endDrag">
        <i class="material-icons gray">drag_indicator</i>
      </div>
      <div class="spacer"
           ref="spacer"
           :style="styleObject"></div>
      <div class="checkbox"
           @click="noteUserAction(); toggleCompletedState()">
        <i v-if="isCompleted" class="material-icons gray">check_box</i>
        <i v-if="!isCompleted" class="material-icons gray">check_box_outline_blank</i>
      </div>
      <editable class="textbox"
                :content="getStrippedTitle()"
                @update:content="updateTitle($event)"
                :class="[{completed: isCompleted}]"
                ref="textbox"
                @click.exact="noteUserAction"
                @keydown.exact="noteUserAction"
                @focus="textboxFocused"
                @blur="textboxBlurred"
                @input="textboxInputted"
                @keydown.down.exact="downPressed"
                @keydown.ctrl.down.exact.prevent="moveTaskDown"
                @keydown.up.exact="upPressed"
                @keydown.ctrl.up.exact.prevent="moveTaskUp"
                @keydown.right="rightPressed"
                @keydown.left="leftPressed"
                @keydown.enter.prevent="enterPressed"
                @keydown.delete="deleteOrBackspacePressed"
                @keydown.tab.exact.prevent="indentTask"
                @keydown.shift.tab.exact.prevent="unindentTask"
                ></editable>
    </div>
    <task ref="subtask"
          :class="{indent: !jsTask.isRoot}"
          v-for="subtask in jsTask.subtasks"
          :key="subtask.id"
          @noteUserAction="noteUserAction"
          :currentListId="currentListId"
          :initialJsTask="subtask"
          :indentLevel="indentLevel + 1"
          :idMap="idMap"
          :updater="updater"
          :density="density"
          @add-new-task="addNewTask"></task>
  </div>
</template>

<script>
import Editable from './Editable.vue'

export default {
  name: 'task',

  components: {
    Editable
  },

  props: [
    'currentListId',
    'initialJsTask',
    'indentLevel',
    'idMap',
    'updater',
    'density'
  ],

  data () {
    return {
      debug: false,

      jsTask: this.initialJsTask,

      // True if this task is currently active.
      selected: false,

      // True if a drag is in progress and this task is the drop target.
      draggedOver: false,

      // True if the mouse is hovering over the thistask element.
      hovered: false,

      // The text that was last saved on the server.
      lastSavedText: this.initialJsTask.gTask.title,

      // How much the indent should be adjusted. Used for indent/unindent
      // actions.
      indentAdjustment: 0
    }
  },

  computed: {
    isCompleted: function () {
      return this.jsTask.gTask.status === 'completed'
    },

    styleObject: function () {
      return {
        width: this.indentLevel.toString() + 'em',
        height: '0.625em',
        float: 'left'
      }
    }
  },

  mounted: function () {
    this.debouncedSaveText = this.debounce(() => { this.saveText() }, 2000)
  },

  methods: {
    // Returns the indentation level of this task.
    getIndentLevel: function () {
      let indentLevel = 0
      let parentEl = this.$parent
      if (parentEl.jsTask == null) {
        return -1
      }
      while (!parentEl.jsTask.isRoot) {
        indentLevel += 1
        parentEl = parentEl.$parent
      }
      return indentLevel
    },

    // Runs the provided function on this task and all its children, with
    // depth-first traversal. func takes a Task object as its only argument.
    runOnTaskAndChildren: function (func) {
      func(this)
      // this.$refs.subtask is only defined if a subtask component exists.
      // It's not an empty list, like you would expect.
      if (this.$refs.subtask) {
        for (let subtask of this.$refs.subtask) {
          subtask.runOnTaskAndChildren(func)
        }
      }
    },

    // The same as runOnTaskAndChildren, but in reverse order (bottom to top).
    runOnTaskAndChildrenReverse: function (func) {
      // this.$refs.subtask is only defined if a subtask component exists.
      // It's not an empty list, like you would expect.
      if (this.$refs.subtask) {
        for (let i = this.$refs.subtask.length - 1; i >= 0; i--) {
          let subtask = this.$refs.subtask[i]
          subtask.runOnTaskAndChildrenReverse(func)
        }
      }
      func(this)
    },

    // Returns a function, that, as long as it continues to be invoked, will
    // not be triggered. The function will be called after it stops being
    // called for wait milliseconds.
    debounce: function (func, wait) {
      let timeout

      return function executedFunction () {
        let context = this
        let args = arguments
        let later = function () {
          timeout = null
          func.apply(context, args)
        }
        clearTimeout(timeout)
        timeout = setTimeout(later, wait)
      }
    },

    getStrippedTitle: function () {
      let numIndents = this.jsTask.gTask.title.match(/^[~]*/)[0].length
      return this.jsTask.gTask.title.substring(numIndents)
    },

    updateTitle: function (newTitle) {
      this.jsTask.gTask.title = '~'.repeat(Math.max(0, this.indentLevel + this.indentAdjustment)) + newTitle
    },

    noteUserAction: function () {
      this.$emit('noteUserAction')
    },

    maybeCheckResult: function (gTask, response) {
      if (this.debug) {
        let equivResult = this.$equivGTasks(gTask, response.result)
        if (!equivResult.success) {
          console.error(equivResult.message)
        }
      }
    },

    // Note, this method does not handle flushing the update queue.
    sendUpdateToServer: function () {
      this.updater.newUpdate(
        this.currentListId,
        this.jsTask,
        response => {
          if (response.status === 200) {
            this.$set(this.jsTask.gTask, 'etag', response.result.etag)
            if (this.jsTask.gTask.status === 'completed') {
              this.$set(this.jsTask.gTask, 'completed', response.result.completed)
            }
            this.lastSavedText = response.result.title
            this.maybeCheckResult(this.jsTask.gTask, response)
          } else {
            console.error('Failed to update task ' + this.jsTask.id)
          }
        }
      )
    },

    toggleCompletedState: function () {
      if (this.jsTask.deleted) {
        return
      }
      this.updater.waitForFlush()
      if (!this.isCompleted) {
        this.markCompleted()
      } else {
        this.markNeedsAction()
      }
      this.updater.flush()
    },

    markCompleted: function () {
      // The completed timestamp field will be set when the response arrives.
      this.$set(this.jsTask.gTask, 'status', 'completed')
      this.sendUpdateToServer()
      // this.$refs.subtask is only defined if a subtask component exists. It's
      // not an empty list, like you would expect.
      if (this.$refs.subtask) {
        for (let subtask of this.$refs.subtask) {
          subtask.markCompleted()
        }
      }
    },

    markNeedsAction: function () {
      this.$set(this.jsTask.gTask, 'completed', undefined)
      this.$set(this.jsTask.gTask, 'status', 'needsAction')
      this.sendUpdateToServer()
      // this.$refs.subtask is only defined if a subtask component exists. It's
      // not an empty list, like you would expect.
      if (this.$refs.subtask) {
        for (let subtask of this.$refs.subtask) {
          subtask.markNeedsAction()
        }
      }
    },

    // For the hover and select methods, we must make sure that the task is not
    // both "hovered" and "selected", so whenever the task is selected, it is
    // not considered hovered, even if the mouse is over it. This is needed
    // because otherwise the state will change and the task will be rerendered
    // when the mouse moves away from the selected task, and the textbox data
    // will be reset to the gTask's value. But the user might have typed
    // something in, so we don't want to lose that data when moving the mouse.

    hoverTask: function () {
      if (!this.selected) {
        this.hovered = true
      }
    },

    unhoverTask: function () {
      if (this.hovered) {
        this.hovered = false
      }
    },

    selectTask: function () {
      this.selected = true
      this.hovered = false
    },

    unselectTask: function () {
      this.selected = false
    },

    textboxFocused: function (event) {
      this.selectTask()
    },

    textboxBlurred: function (event) {
      this.unselectTask()
      this.saveText(event)
    },

    textboxInputted: function (event) {
      event.target.style.height = 'auto'
      event.target.style.height = (event.target.scrollHeight) + 'px'
      this.debouncedSaveText()
    },

    saveText: function () {
      if (this.jsTask.deleted) {
        return
      }
      if (this.jsTask.gTask.title !== this.lastSavedText) {
        this.sendUpdateToServer()
        this.updater.flush()
      }
    },

    // Insert a new task. If there are subtasks, then put the new task at the
    // start of the subtasks. Otherwise add the task immediately after this task.
    enterPressed: function (event) {
      let textbox = this.$refs.textbox
      let startAndEnd = textbox.getSelectionPositions()
      if (startAndEnd[0] === startAndEnd[1] && startAndEnd[0] === 0 && textbox.length() > 0) {
        // The cursor is at the beginning of the non-empty line, so add the new
        // task before this one.
        let parentJsTask = this.$parent.jsTask
        let positionInParent = parentJsTask.subtasks.findIndex(
          task => { return task.id === this.jsTask.id })
        if (positionInParent < 0) {
          console.error('There is some error, jsTask ' + this.jsTask.id +
            ' does not seem to exist in its parent')
          return
        }
        if (positionInParent === 0) {
          this.$parent.addNewTask(null)
        } else {
          this.$parent.addNewTask(parentJsTask.subtasks[positionInParent - 1])
        }
      } else {
        // The cursor was not at the beginning of the line, so put the new task
        // after this one.
        if (this.jsTask.subtasks.length > 0) {
          this.addNewTask(null)
        } else {
          this.$emit('add-new-task', this.jsTask)
        }
      }
    },

    // Note, this method does not handle flushing the update queue.
    sendInsertToServer: function (parentJsTask, previousJsTask, newJsTask) {
      let previousJsTaskForInsert = previousJsTask == null ? parentJsTask : previousJsTask
      if (previousJsTaskForInsert != null && previousJsTaskForInsert.isRoot) {
        previousJsTaskForInsert = null
      }

      this.updater.newInsert(
        this.currentListId,
        newJsTask,
        previousJsTaskForInsert,
        response => {
          if (response.status === 200) {
            this.idMap.update(newJsTask.id, newJsTask.gTask.id)
            // These fields may have changed between when the insert was sent
            // and when the response was received, so the gTask needs to be
            // updated again after the response is processed.
            let title = newJsTask.gTask.title
            let status = newJsTask.gTask.status
            // Use assign to copy the properties in, but keep the existing
            // gTask object, since the object can't change or else in-progress
            // actions have a reference to the old gTask.
            Object.assign(newJsTask.gTask, response.result)
            this.$set(newJsTask.gTask, 'title', title)
            this.$set(newJsTask.gTask, 'status', status)
            this.maybeCheckResult(newJsTask.gTask, response)
          } else {
            console.error('Failed to insert task ' + newJsTask.id)
          }
        }
      )
    },

    // Inserts a new task after the provided task.
    addNewTask: function (previousJsTask) {
      let newGTask = this.$newGTask()
      newGTask.title = '~'.repeat(this.getIndentLevel() + 1)
      let newJsTask = new this.$JsTask(this.idMap.newJsTask(), newGTask)

      if (previousJsTask === null) {
        // Insert as the first subtask of this task.
        this.jsTask.subtasks.splice(0, 0, newJsTask)
        this.sendInsertToServer(this.jsTask, null, newJsTask)
      } else {
        for (let i = 0; i < this.jsTask.subtasks.length; i++) {
          if (this.jsTask.subtasks[i].id === previousJsTask.id) {
            this.jsTask.subtasks.splice(i + 1, 0, newJsTask)
            break
          }
        }
        this.sendInsertToServer(this.jsTask, previousJsTask, newJsTask)
      }
      this.updater.flush()

      // Focus the keyboard on the new element.
      this.$nextTick(() => {
        for (let subtask of this.$refs.subtask) {
          if (subtask.jsTask.id === newJsTask.id) {
            subtask.$refs.textbox.focusAndCaretAtStart()
            break
          }
        }
      })
    },

    deleteOrBackspacePressed: function (event) {
      if (this.jsTask.deleted) {
        return
      }
      if (event.target.textContent === '') {
        this.$set(this.jsTask, 'deleted', true)
        let parentJsTask = this.$parent.jsTask
        let positionInParent = parentJsTask.subtasks.findIndex(
          task => { return task.id === this.jsTask.id })
        if (positionInParent < 0) {
          console.error('There is some error, jsTask ' + this.jsTask.id +
            ' does not seem to exist in its parent')
          return
        }

        // Once this task is deleted, all of its children must be reduced one
        // sublevel.
        this.runOnTaskAndChildren((task) => {
          task.indentAdjustment = -1
          task.updateTitle(task.getStrippedTitle())
          task.sendUpdateToServer()
        })
        this.updater.flush()

        // Delete this task on the server.
        this.updater.newDelete(
          this.currentListId,
          this.jsTask,
          response => {
            if (response.status !== 204) {
              console.error('Failed to delete task ' + this.jsTask.id)
            }
          }
        )
        this.updater.flush()

        this.focusOneTaskUp('end')

        // Locally delete this task (second arg=1) and move its subtasks to the
        // parent.
        parentJsTask.subtasks.splice(positionInParent, 1, ...this.jsTask.subtasks)
      }
    },

    // Make this task a subtask of the one above.
    indentTask: function () {
      // In order for this task to be indented, it must not be the first
      // subtask of the parent, because then there's no new parent for it to
      // have.
      let parentJsTask = this.$parent.jsTask
      let positionInParent = parentJsTask.subtasks.findIndex(
        task => { return task.id === this.jsTask.id })
      if (positionInParent < 0) {
        console.error('There is some error, jsTask ' + this.jsTask.id +
          ' does not seem to exist in its parent')
        return
      } else if (positionInParent === 0) {
        return
      }

      // Keep track of the cursor position, so that it can be restored after
      // the task is moved.
      let startAndEnd = this.$refs.textbox.getSelectionPositions()

      let newParentJsTask = parentJsTask.subtasks[positionInParent - 1]

      // Locally move this task.
      parentJsTask.subtasks.splice(positionInParent, 1)
      newParentJsTask.subtasks.push(this.jsTask)
      this.$nextTick(() => {
        let refocused = false
        for (let parentSubtask of this.$parent.$refs.subtask) {
          if (parentSubtask.jsTask.id === newParentJsTask.id) {
            for (let subtask of parentSubtask.$refs.subtask) {
              if (subtask.jsTask.id === this.jsTask.id) {
                subtask.$refs.textbox.focusAndSetSelection(startAndEnd[0], startAndEnd[1])
                refocused = true
                break
              }
            }
          }
          if (refocused) {
            break
          }
        }
      })

      // Now that the task has been moved, and so it has a different indent
      // level locally, move this task and its subtasks on the server.
      this.runOnTaskAndChildren((task) => {
        task.indentAdjustment = 1
        task.updateTitle(task.getStrippedTitle())
        task.sendUpdateToServer()
      })
      this.updater.flush()
    },

    // Move this task up one level in the tree. Its children move along with
    // it. If this task has siblings directly after it, then those siblings are
    // moved to underneath this task's last subtask. E.g. (moving b)
    // a           a
    //  b    =>    b
    //   c          c
    //  d           d
    //   e           e
    unindentTask: function () {
      let parentJsTask = this.$parent.jsTask
      if (parentJsTask.isRoot) {
        // If this task is a subtask of the root item, then it can't actually
        // be unindented any more. This is because all top-level items are
        // subtasks of the root item.
        return
      }

      let positionInParent = parentJsTask.subtasks.findIndex(
        task => { return task.id === this.jsTask.id })
      if (positionInParent < 0) {
        console.error('There is some error, jsTask ' + this.jsTask.id +
          ' does not seem to exist in its parent')
        return
      }

      // Keep track of the cursor position, so that it can be restored after
      // the task is moved.
      let startAndEnd = this.$refs.textbox.getSelectionPositions()

      // The new parent is the grandparent. We know that this is a Task because
      // the parent is not the root item.
      let newParentJsTask = this.$parent.$parent.jsTask
      // The position in the new parent is immediately after this task's parent.
      let positionInNewParent = newParentJsTask.subtasks.findIndex(
        task => { return task.id === parentJsTask.id }) + 1

      // Locally move all the later siblings to underneath this task. This does
      // not need to be done on the server because the indent level of them does
      // not change.
      this.jsTask.subtasks.push(...parentJsTask.subtasks.splice(positionInParent + 1))

      // Locally move this task.
      parentJsTask.subtasks.splice(positionInParent, 1)
      newParentJsTask.subtasks.splice(positionInNewParent, 0, this.jsTask)
      this.$nextTick(() => {
        for (let subtask of this.$parent.$parent.$refs.subtask) {
          if (subtask.jsTask.id === this.jsTask.id) {
            subtask.$refs.textbox.focusAndSetSelection(startAndEnd[0], startAndEnd[1])
            break
          }
        }
      })

      // Now that the task has been moved locally, and so it has a different
      // indent level locally, move the task and subtasks on the server.
      this.runOnTaskAndChildren((task) => {
        task.indentAdjustment = -1
        task.updateTitle(task.getStrippedTitle())
        task.sendUpdateToServer()
      })
      this.updater.flush()
    },

    downPressed: function (event) {
      this.focusOneTaskDown('end')
    },

    upPressed: function (event) {
      this.focusOneTaskUp('end')
    },

    rightPressed: function (event) {
      // If the cursor is at the end of the text box, then move to the next
      // task.
      let textbox = this.$refs.textbox
      let startAndEnd = textbox.getSelectionPositions()
      let length = textbox.length()
      if (startAndEnd[0] === startAndEnd[1] && startAndEnd[0] === length) {
        this.focusOneTaskDown('start')
      }
    },

    leftPressed: function (event) {
      // If the cursor is at the beginning of the text box, then move to the
      // previous task.
      let textbox = this.$refs.textbox
      let startAndEnd = textbox.getSelectionPositions()
      if (startAndEnd[0] === startAndEnd[1] && startAndEnd[0] === 0) {
        this.focusOneTaskUp('end')
      }
    },

    // Put the focus on the text box immediately below this one.
    // cursorPos can be 'start' or 'end', indicating whether to place the
    // cursor at the beginning or end of the task text.
    focusOneTaskDown: function (cursorPos) {
      let newTaskEl = null

      // If this task has subtasks, then the first subtask is immediately
      // below.
      if (this.jsTask.subtasks.length > 0) {
        let jsTaskId = this.jsTask.subtasks[0].id
        for (let subtask of this.$refs.subtask) {
          if (subtask.jsTask.id === jsTaskId) {
            newTaskEl = subtask
            break
          }
        }
      }

      // If there are no subtasks, then check to see if there are any
      // subsequent siblings. If not, go up one level and check again, and keep
      // going until one is found.
      if (newTaskEl === null) {
        let previousJsTaskId = this.jsTask.id
        let parentEl = this.$parent
        while (true) {
          let positionInParent = parentEl.jsTask.subtasks.findIndex(
            task => { return task.id === previousJsTaskId })
          if (positionInParent >= 0 && parentEl.jsTask.subtasks.length > positionInParent + 1) {
            // There is at least 1 task after this one.
            let jsTaskId = parentEl.jsTask.subtasks[positionInParent + 1].id
            for (let subtask of parentEl.$refs.subtask) {
              if (subtask.jsTask.id === jsTaskId) {
                newTaskEl = subtask
                break
              }
            }
          }
          if (newTaskEl !== null) {
            break
          }
          if (parentEl.jsTask.isRoot) {
            // We can't go above the root of the tree.
            return
          }
          previousJsTaskId = parentEl.jsTask.id
          parentEl = parentEl.$parent
        }
      }

      if (newTaskEl !== null) {
        this.$nextTick(() => {
          if (cursorPos === 'start') {
            newTaskEl.$refs.textbox.focusAndCaretAtStart()
          } else if (cursorPos === 'end') {
            newTaskEl.$refs.textbox.focusAndCaretAtEnd()
          }
        })
      }
    },

    // Moves the task position down one. This will only move the task among
    // siblings, it will not reparent the task.
    moveTaskDown: function () {
      let parentTask = this.$parent.jsTask
      let positionInParent = parentTask.subtasks.findIndex(
        task => { return task.id === this.jsTask.id })
      if (positionInParent < 0 || positionInParent >= parentTask.subtasks.length - 1) {
        return
      }
      let newPreviousJsTask = parentTask.subtasks[positionInParent + 1]
      while (newPreviousJsTask.subtasks.length !== 0) {
        newPreviousJsTask = newPreviousJsTask.subtasks[newPreviousJsTask.subtasks.length - 1]
      }

      // Keep track of the cursor position, so that it can be restored after
      // the task is moved.
      let startAndEnd = this.$refs.textbox.getSelectionPositions()

      // Move the tasks on the server. Each move must be done individually, or
      // else the server will assign the same position to all of the tasks
      // (including the children) and they will be displayed wrong.
      this.runOnTaskAndChildrenReverse((task) => {
        task.updater.newMove(
          task.currentListId,
          task.jsTask,
          newPreviousJsTask,
          response => {
            if (response.status === 200) {
              task.$set(task.jsTask.gTask, 'etag', response.result.etag)
              task.$set(task.jsTask.gTask, 'parent', response.result.parent)
              task.$set(task.jsTask.gTask, 'position', response.result.position)
              task.maybeCheckResult(task.jsTask.gTask, response)
            } else {
              console.error('Failed to move task ' + task.jsTask.id + ' down one')
            }
          }
        )
        task.updater.flush()
      })

      // Move the task locally.
      this.$set(parentTask.subtasks, positionInParent, parentTask.subtasks[positionInParent + 1])
      this.$set(parentTask.subtasks, positionInParent + 1, this.jsTask)
      this.$nextTick(() => {
        for (let subtask of this.$parent.$refs.subtask) {
          if (subtask.jsTask.id === this.jsTask.id) {
            subtask.$refs.textbox.focusAndSetSelection(startAndEnd[0], startAndEnd[1])
            break
          }
        }
      })
    },

    // Moves the task position up one. This will only move the task among
    // siblings, it will not reparent the task.
    moveTaskUp: function () {
      let parentTask = this.$parent.jsTask
      let positionInParent = parentTask.subtasks.findIndex(
        task => { return task.id === this.jsTask.id })
      if (positionInParent <= 0) {
        return
      }
      // If positionInParent === 1, then moving up 1 will move it to the first
      // position, which means the previous task is the parent.
      let newPreviousJsTask = parentTask
      if (positionInParent > 1) {
        newPreviousJsTask = parentTask.subtasks[positionInParent - 2]
      }
      if (!newPreviousJsTask.isRoot && newPreviousJsTask !== parentTask) {
        while (newPreviousJsTask.subtasks.length !== 0) {
          newPreviousJsTask = newPreviousJsTask.subtasks[newPreviousJsTask.subtasks.length - 1]
        }
      }

      // Keep track of the cursor position, so that it can be restored after
      // the task is moved.
      let startAndEnd = this.$refs.textbox.getSelectionPositions()

      // Move the tasks on the server. Each move must be done individually, or
      // else the server will assign the same position to all of the tasks
      // (including the children) and they will be displayed wrong.
      this.runOnTaskAndChildrenReverse((task) => {
        task.updater.newMove(
          task.currentListId,
          task.jsTask,
          newPreviousJsTask,
          response => {
            if (response.status === 200) {
              task.$set(task.jsTask.gTask, 'etag', response.result.etag)
              task.$set(task.jsTask.gTask, 'parent', response.result.parent)
              task.$set(task.jsTask.gTask, 'position', response.result.position)
              task.maybeCheckResult(task.jsTask.gTask, response)
            } else {
              console.error('Failed to move task ' + task.jsTask.id + ' up one')
            }
          }
        )
        task.updater.flush()
      })

      // Move the task locally.
      let tmp = parentTask.subtasks[positionInParent - 1]
      this.$set(parentTask.subtasks, positionInParent - 1, this.jsTask)
      this.$set(parentTask.subtasks, positionInParent, tmp)
      this.$nextTick(() => {
        for (let subtask of this.$parent.$refs.subtask) {
          if (subtask.jsTask.id === this.jsTask.id) {
            subtask.$refs.textbox.focusAndSetSelection(startAndEnd[0], startAndEnd[1])
            break
          }
        }
      })
    },

    // Given a Vue task element which represents the root of a (sub-)tree in
    // the task list, find the "last" task, which is the last subtask of the
    // last subtask of the last subtask...  Returns the Vue task element
    // corresponding to that one.
    lastTaskInTree: function (rootElement) {
      // if (rootElement.jsTask.subtasks.length === 0) {
      //   return rootElement
      // }
      let taskElement = rootElement
      while (true) {
        let task = taskElement.jsTask
        if (task.subtasks.length === 0) {
          return taskElement
        }
        let lastSubtask = task.subtasks[task.subtasks.length - 1]
        for (let subtask of taskElement.$refs.subtask) {
          if (subtask.jsTask.id === lastSubtask.id) {
            taskElement = subtask
            break
          }
        }
        // If the task wasn't found, something is wrong, so return null so that
        // we don't enter an infinite loop.
        if (taskElement.jsTask.id !== lastSubtask.id) {
          return null
        }
      }
    },

    // Put the focus on the text box immediately above this one.
    // cursorPos can be 'start' or 'end', indicating whether to place the
    // cursor at the beginning or end of the task text.
    focusOneTaskUp: function (cursorPos) {
      // If this is the first task in the list, then there is nothing above.
      if (this.$parent.jsTask.isRoot && this.$parent.jsTask.subtasks[0].id === this.jsTask.id) {
        return
      }

      let newTaskEl = null

      // If this task has siblings, check if there is one above, and if there
      // is, then find it's last transitive subtask.
      let parentEl = this.$parent
      let positionInParent = parentEl.jsTask.subtasks.findIndex(
        task => { return task.id === this.jsTask.id })
      if (positionInParent < 0) {
        console.debug('Did not find task ' + this.jsTask.id.toString() + ' in subtasks of task ' + parentEl.jsTask.id.toString() + ' but it should be there')
        return
      }
      if (positionInParent > 0) {
        let aboveId = parentEl.jsTask.subtasks[positionInParent - 1].id
        let aboveEl = null
        for (let subtask of parentEl.$refs.subtask) {
          if (subtask.jsTask.id === aboveId) {
            aboveEl = subtask
            break
          }
        }
        let lastTask = this.lastTaskInTree(aboveEl)
        if (lastTask === null) {
          return
        }
        newTaskEl = lastTask
      }

      // There are no siblings, so the parent is the one above.
      if (newTaskEl === null) {
        newTaskEl = this.$parent
      }

      if (newTaskEl !== null) {
        this.$nextTick(() => {
          if (cursorPos === 'start') {
            newTaskEl.$refs.textbox.focusAndCaretAtStart()
          } else if (cursorPos === 'end') {
            newTaskEl.$refs.textbox.focusAndCaretAtEnd()
          }
        })
      }
    },

    // Methods to handle dragging and dropping of tasks. When the drag
    // commences, startDrag is called and stashes the dragged task's element in
    // a global variable, since it's not possible to pass the element inside
    // the event. When the drag ends, regardless of whether it is successful or
    // not, the global variable is reset.

    // Called on the task that started the drag.
    startDrag: function (event) {
      window.draggedTask = this
      event.dataTransfer.dropEffect = 'move'
      event.dataTransfer.setDragImage(this.$el, -25, 10)
    },

    // Called on the task that started the drag, after the drop is completed.
    endDrag: function (event) {
      window.draggedTask = null
    },

    // Called on the task that is being hovered over. Preventing the default
    // means that this task will accept a drop.
    dragOver: function (event) {
      let idOfThisAndParents = []
      let aTaskEl = this
      while (!aTaskEl.jsTask.isRoot) {
        idOfThisAndParents.push(aTaskEl.jsTask.id)
        aTaskEl = aTaskEl.$parent
      }
      if (!idOfThisAndParents.includes(window.draggedTask.jsTask.id)) {
        this.draggedOver = true
        event.preventDefault()
        event.dataTransfer.dropEffect = 'move'
      }
    },

    // Called on the task that is being hovered over.
    dragLeave: function (event) {
      this.draggedOver = false
    },

    // Called on the task that is being dropped on.
    drop: function (event) {
      this.draggedOver = false
      event.preventDefault()

      let taskToMove = window.draggedTask

      // The indentation level of the dragged task and all its children needs
      // to be adjusted.
      let taskToMoveIndentLevel = taskToMove.getIndentLevel()
      let newLocationIndentLevel = this.getIndentLevel()

      // Remove the task from its original location. This must be done first
      // because Vue expects each element's key in a list to be unique, and if
      // this is dropped in another position in the same parent then both the
      // new and old elements' keys will conflict.
      let parentJsTask = taskToMove.$parent.jsTask
      let positionInParent = parentJsTask.subtasks.findIndex(
        task => { return task.id === taskToMove.jsTask.id })
      if (positionInParent < 0) {
        console.error('There is some error, jsTask ' + taskToMove.jsTask.id +
          ' does not seem to exist in its parent')
        return
      }
      parentJsTask.subtasks.splice(positionInParent, 1)

      // Now that the task is removed from the original location, it can be put
      // in the new location.
      if (this.jsTask.subtasks.length > 0) {
        this.jsTask.subtasks.splice(0, 0, taskToMove.jsTask)
        newLocationIndentLevel += 1
      } else {
        // The task to drop on has no subtasks, so put the dragged task
        // immediately after it as a sibling.
        let parentJsTask = this.$parent.jsTask
        let positionInParent = parentJsTask.subtasks.findIndex(
          task => { return task.id === this.jsTask.id })
        if (positionInParent < 0) {
          console.error('There is some error, jsTask ' + this.jsTask.id +
            ' does not seem to exist in its parent')
          return
        }
        parentJsTask.subtasks.splice(positionInParent + 1, 0, taskToMove.jsTask)
      }

      // Move the task on the server.
      taskToMove.runOnTaskAndChildrenReverse((task) => {
        task.indentAdjustment = newLocationIndentLevel - taskToMoveIndentLevel
        task.updateTitle(task.getStrippedTitle())
        task.sendUpdateToServer()
        task.updater.flush()

        task.updater.newMove(
          task.currentListId,
          task.jsTask,
          this.jsTask,
          response => {
            if (response.status === 200) {
              task.$set(task.jsTask.gTask, 'etag', response.result.etag)
              task.$set(task.jsTask.gTask, 'parent', response.result.parent)
              task.$set(task.jsTask.gTask, 'position', response.result.position)
              task.maybeCheckResult(task.jsTask.gTask, response)
            } else {
              console.error('Failed to reparent task ' + task.jsTask.id + ' during drag and drop to ' + this.jsTask.id)
            }
          }
        )
        task.updater.flush()
      })
    }
  }
}
</script>

<style scoped>

div.thistask {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding-top: 3px;
  padding-bottom: 1px;
}

div.thistask.desktop.hovered {
  background-color: #e1ffe1;
}

div.thistask.selected {
  background-color: #cbffcb;
}

span.debugid {
  order: 0;
  flex: 0 0 auto;
  width: 15em;
}

div.draghandle {
  order: 1;
  flex: 0 0 auto;
  align-self: stretch;
  margin-right: -2px;
  cursor: grab;
}

.material-icons.gray {
  color: rgba(0, 0, 0, 0.54);
}

div.spacer {
  order: 2;
  flex: 0 0 auto;
}

div.checkbox {
  order: 3;
  flex: 0 0 auto;
  width: 1.25em;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

div.thistask.desktop, div.thistask.desktop .material-icons {
  font-size: 16px;
}

div.thistask.mobile, div.thistask.mobile .material-icons {
  font-size: 24px;
}

.textbox {
  order: 4;
  flex: 1 0 auto;
  outline: none;
  background-color: rgba(0, 0, 0, 0);

  padding: 0;
  border: 0em none black;
  width: 0;  /* Seems weird, but this makes the textbox expand to the right edge of the page */
  resize: none;

  font-family: Arial;
}

div.thistask.desktop .textbox {
  font-size: 13.333px;
}

div.thistask.mobile .textbox {
  font-size: 16px;
  margin-top: 3px;
}

.textbox.completed {
  text-decoration: line-through;
  color: #666;
}

</style>
